/**
 * Reports Monthly Counter animation.
 */

(() => {
	'use strict';

	const selectors = {
		widget: '[data-reports-monthly-counter]',
		bar: '.reports-monthly-counter__bar',
		fill: '.reports-monthly-counter__bar-fill',
		value: '.reports-monthly-counter__value',
		counter: '[data-counter-target]',
	};

	const settings = {
		counterAnimationDuration: 2200,
		barAnimationDelay: 120,
		observerThreshold: 0.3,
	};

	/**
	 * Animates a counter from 0 to the target value.
	 *
	 * @param {HTMLElement} element Counter element.
	 * @param {number} target Target value.
	 * @param {number} duration Animation duration.
	 *
	 * @return {void}
	 */
	const animateCounter = (
		element,
		target,
		duration = settings.counterAnimationDuration
	) => {
		const startTime = performance.now();

		const updateCounter = (currentTime) => {
			const elapsedTime = currentTime - startTime;
			const progress = Math.min(elapsedTime / duration, 1);
			const easedProgress = 1 - Math.pow(1 - progress, 3);
			const currentValue = Math.floor(target * easedProgress);

			element.textContent = currentValue.toString();

			if (progress < 1) {
				window.requestAnimationFrame(updateCounter);
				return;
			}

			element.textContent = target.toString();
		};

		window.requestAnimationFrame(updateCounter);
	};

	/**
	 * Animates all counters inside the widget.
	 *
	 * @param {HTMLElement} widget Widget element.
	 *
	 * @return {void}
	 */
	const animateCounters = (widget) => {
		const counters = widget.querySelectorAll(selectors.counter);

		counters.forEach((counter) => {
			const parsedTarget = Number.parseInt(
				counter.dataset.counterTarget || '0',
				10
			);

			const target = Number.isNaN(parsedTarget)
				? 0
				: parsedTarget;

			animateCounter(counter, target);
		});
	};

	/**
	 * Returns a percentage limited to the range from 0 to 100.
	 *
	 * @param {string|number} value Percentage value.
	 *
	 * @return {number}
	 */
	const getSafePercentage = (value) => {
		const parsedValue = Number.parseFloat(value);

		if (Number.isNaN(parsedValue)) {
			return 0;
		}

		return Math.min(Math.max(parsedValue, 0), 100);
	};

	/**
	 * Returns the target width of a bar.
	 *
	 * The attribute can exist on the fill or bar element.
	 *
	 * @param {HTMLElement} bar Bar element.
	 * @param {HTMLElement} fill Fill element.
	 *
	 * @return {number}
	 */
	const getTargetWidth = (bar, fill) => {
		const targetWidth =
			fill.dataset.targetWidth ||
			bar.dataset.targetWidth ||
			'0';

		return getSafePercentage(targetWidth);
	};

	/**
	 * Resets all bars and values before animation.
	 *
	 * @param {HTMLElement} widget Widget element.
	 *
	 * @return {void}
	 */
	const prepareBars = (widget) => {
		const bars = widget.querySelectorAll(selectors.bar);

		bars.forEach((bar) => {
			const fill = bar.querySelector(selectors.fill);
			const value = bar.querySelector(selectors.value);

			if (fill) {
				fill.style.width = '0%';
			}

			if (value) {
				value.style.left = 'var(--bar-value-offset, 8px)';
			}
		});
	};

	/**
	 * Animates all bars and moves values to the end of each bar.
	 *
	 * @param {HTMLElement} widget Widget element.
	 *
	 * @return {void}
	 */
	const animateBars = (widget) => {
		const bars = widget.querySelectorAll(selectors.bar);

		bars.forEach((bar, index) => {
			const fill = bar.querySelector(selectors.fill);
			const value = bar.querySelector(selectors.value);

			if (!fill) {
				return;
			}

			const targetWidth = getTargetWidth(bar, fill);

			window.setTimeout(() => {
				fill.style.width = `${targetWidth}%`;

				if (value) {
					value.style.left =
						`calc(${targetWidth}% + var(--bar-value-offset, 8px))`;
				}
			}, settings.barAnimationDelay * index);
		});
	};

	/**
	 * Runs the animation once.
	 *
	 * @param {HTMLElement} widget Widget element.
	 *
	 * @return {void}
	 */
	const runAnimation = (widget) => {
		if (widget.dataset.animated === 'true') {
			return;
		}

		widget.dataset.animated = 'true';

		prepareBars(widget);

		window.requestAnimationFrame(() => {
			window.requestAnimationFrame(() => {
				animateBars(widget);
				animateCounters(widget);
			});
		});
	};

	/**
	 * Initializes a widget.
	 *
	 * @param {HTMLElement} widget Widget element.
	 *
	 * @return {void}
	 */
	const initWidget = (widget) => {
		if (widget.dataset.monthlyCounterInitialized === 'true') {
			return;
		}

		widget.dataset.monthlyCounterInitialized = 'true';

		prepareBars(widget);

		if (!('IntersectionObserver' in window)) {
			runAnimation(widget);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) {
						return;
					}

					runAnimation(entry.target);
					observer.unobserve(entry.target);
				});
			},
			{
				threshold: settings.observerThreshold,
			}
		);

		observer.observe(widget);
	};

	/**
	 * Initializes all widgets in the specified scope.
	 *
	 * @param {Document|HTMLElement} scope Search scope.
	 *
	 * @return {void}
	 */
	const init = (scope = document) => {
		let widgets = [];

		if (
			scope instanceof HTMLElement &&
			scope.matches(selectors.widget)
		) {
			widgets = [scope];
		} else {
			widgets = scope.querySelectorAll(selectors.widget);
		}

		widgets.forEach((widget) => {
			initWidget(widget);
		});
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => {
			init();
		});
	} else {
		init();
	}

	/**
	 * Elementor frontend support.
	 */
	window.addEventListener('elementor/frontend/init', () => {
		if (
			typeof window.elementorFrontend === 'undefined' ||
			!window.elementorFrontend.hooks
		) {
			return;
		}

		window.elementorFrontend.hooks.addAction(
			'frontend/element_ready/global',
			($scope) => {
				const scopeElement = $scope?.[0];

				if (!scopeElement) {
					return;
				}

				init(scopeElement);
			}
		);
	});
})();